import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [overflowMenu] - The overflow menu (`overflow-list`), when used.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  /** Optional: only present when `overflow-list` is rendered (default Dawn menu). Custom mega menus omit it. */
  requiredRefs = [];

  #abortController = new AbortController();
  /** @type {MediaQueryList | null} */
  #belowDesktopQuery = null;
  #scrollLockedByMenu = false;
  #mouseLeaveTimeout = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Disable scroll when hovering over menu (desktop only)
    this.addEventListener('mouseenter', this.#handleMouseEnter, {
      signal: this.#abortController.signal,
    });
    
    this.addEventListener('mouseleave', this.#handleMouseLeave, {
      signal: this.#abortController.signal,
    });

    // Prevent wheel scroll when menu is hovered (desktop only)
    this.addEventListener('wheel', this.#handleWheel, {
      signal: this.#abortController.signal,
      passive: false,
    });

    // Enable scroll if the viewport drops below the desktop breakpoint (< 1024px).
    // A matchMedia 'change' listener only fires when that breakpoint is crossed, instead of a
    // window 'resize' listener running on every resize event.
    this.#belowDesktopQuery = window.matchMedia('(max-width: 1023px)');
    if (this.#belowDesktopQuery.addEventListener) {
      this.#belowDesktopQuery.addEventListener('change', this.#handleResize, {
        signal: this.#abortController.signal,
      });
    } else {
      this.#belowDesktopQuery.addListener(this.#handleResize); // Safari < 14
    }

    // Ensure scroll is enabled when the page is left (navigation, tab close, bfcache).
    // 'pagehide' instead of 'beforeunload': beforeunload listeners can exclude the page from the
    // back/forward cache (e.g. Firefox), so Back/Forward did a full reload instead of an instant
    // restore. pagehide fires in the same situations (and also when entering the bfcache).
    window.addEventListener('pagehide', this.#handlePageHide, {
      signal: this.#abortController.signal,
    });

    // Handle visibility change (tab switch)
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, {
      signal: this.#abortController.signal,
    });

    // Load mega-menu images on the first sign of intent (pointer enters the nav, or keyboard
    // focus) instead of on page load. Loading them on page load downloaded ~90-110 menu images
    // on every page, competing with the hero/LCP image, even for shoppers who never open a menu.
    // Panels open after a short hover delay, so images still start loading before they show.
    this.addEventListener('pointerenter', this.#preloadImages, {
      once: true,
      signal: this.#abortController.signal,
    });
    this.addEventListener('focusin', this.#preloadImages, {
      once: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Clear any pending timeouts
    if (this.#mouseLeaveTimeout) {
      clearTimeout(this.#mouseLeaveTimeout);
      this.#mouseLeaveTimeout = null;
    }
    
    // Ensure body scroll is enabled when component is disconnected
    if (this.#scrollLockedByMenu) {
      this.#enableBodyScroll();
    }

    this.#abortController.abort();
    // Safari < 14 fallback listener isn't covered by the AbortController
    if (this.#belowDesktopQuery && !this.#belowDesktopQuery.addEventListener) {
      this.#belowDesktopQuery.removeListener(this.#handleResize);
    }
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Handle mouse enter on menu - disable scroll
   */
  #handleMouseEnter = () => {
    try {
      // Clear any pending mouse leave timeout
      if (this.#mouseLeaveTimeout) {
        clearTimeout(this.#mouseLeaveTimeout);
        this.#mouseLeaveTimeout = null;
      }

      // Only disable on desktop (when window width is large enough)
      if (!this.#belowDesktopQuery?.matches && !this.#scrollLockedByMenu) {
        this.#disableBodyScroll();
      }
    } catch (error) {
      console.warn('HeaderMenu: Error in handleMouseEnter', error);
    }
  };

  /**
   * Handle mouse leave on menu - enable scroll after delay
   */
  #handleMouseLeave = (event) => {
    try {
      // Clear any existing timeout
      if (this.#mouseLeaveTimeout) {
        clearTimeout(this.#mouseLeaveTimeout);
      }

      // Check if mouse is moving to a related target (submenu)
      const relatedTarget = event.relatedTarget;
      if (relatedTarget && (this.contains(relatedTarget) || this.shadowRoot?.contains(relatedTarget))) {
        return; // Still within menu area
      }
      
      // Wait a bit to see if user is moving to submenu or menu is closing
      this.#mouseLeaveTimeout = setTimeout(() => {
        // Only enable if menu is not expanded and not hovered and we locked it
        if (this.#scrollLockedByMenu && this.ariaExpanded !== 'true' && !this.matches(':hover')) {
          this.#enableBodyScroll();
        }
        this.#mouseLeaveTimeout = null;
      }, DEACTIVATE_DELAY + 50);
    } catch (error) {
      console.warn('HeaderMenu: Error in handleMouseLeave', error);
    }
  };

  /**
   * Prevent wheel scroll when menu is hovered (desktop only)
   * But allow scrolling within scrollable menu content
   */
  #handleWheel = (event) => {
    try {
      // Only prevent on desktop when menu is hovered and we've locked scroll
      if (!this.#belowDesktopQuery?.matches && this.#scrollLockedByMenu && this.matches(':hover')) {
        // Check if the event target is within a scrollable element
        const target = event.target;
        if (target instanceof Element) {
          // Find if target or its parent is a scrollable element
          let element = target;
          while (element && element !== this) {
            const scrollHeight = element.scrollHeight;
            const clientHeight = element.clientHeight;
            const scrollWidth = element.scrollWidth;
            const clientWidth = element.clientWidth;

            // Cheap size check first: an element whose content doesn't overflow can't scroll, so
            // skip getComputedStyle for it. Previously every ancestor's computed style was read on
            // every wheel event; the result of the checks below is unchanged.
            const hasOverflowingContent = scrollHeight > clientHeight || scrollWidth > clientWidth;
            const computedStyle = hasOverflowingContent ? window.getComputedStyle(element) : null;
            const overflowY = computedStyle?.overflowY;
            const overflowX = computedStyle?.overflowX;

            // Check if element is scrollable
            if (
              (overflowY === 'auto' || overflowY === 'scroll') ||
              (overflowX === 'auto' || overflowX === 'scroll')
            ) {

              // If element can scroll vertically or horizontally, allow the event
              if (
                (scrollHeight > clientHeight && (event.deltaY !== 0 || event.deltaX === 0)) ||
                (scrollWidth > clientWidth && (event.deltaX !== 0 || event.deltaY === 0))
              ) {
                // Check if element is at scroll boundaries
                const isAtTop = element.scrollTop <= 0;
                const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
                const isAtLeft = element.scrollLeft <= 0;
                const isAtRight = element.scrollLeft + element.clientWidth >= element.scrollWidth - 1;
                
                // If scrolling up and at top, or scrolling down and at bottom, prevent
                // If scrolling left and at left, or scrolling right and at right, prevent
                if (
                  (event.deltaY > 0 && isAtBottom) ||
                  (event.deltaY < 0 && isAtTop) ||
                  (event.deltaX > 0 && isAtRight) ||
                  (event.deltaX < 0 && isAtLeft)
                ) {
                  // At boundary, prevent to avoid body scroll
                  event.preventDefault();
                }
                // Otherwise allow scrolling within the element
                return;
              }
            }
            
            element = element.parentElement;
          }
        }
        
        // If not within a scrollable element, prevent body scroll
        event.preventDefault();
      }
    } catch (error) {
      console.warn('HeaderMenu: Error in handleWheel', error);
    }
  };

  /**
   * Handle the viewport crossing the desktop breakpoint - enable scroll if now below 1024px
   */
  #handleResize = () => {
    try {
      // If resized to mobile and scroll is locked, enable it
      if (this.#belowDesktopQuery?.matches && this.#scrollLockedByMenu) {
        this.#enableBodyScroll();
      }
    } catch (error) {
      console.warn('HeaderMenu: Error in handleResize', error);
    }
  };

  /**
   * Handle page hide (navigation away, tab close, entering bfcache) - ensure scroll is enabled
   */
  #handlePageHide = () => {
    try {
      if (this.#scrollLockedByMenu) {
        this.#enableBodyScroll();
      }
    } catch (error) {
      console.warn('HeaderMenu: Error in handlePageHide', error);
    }
  };

  /**
   * Handle visibility change - enable scroll if tab becomes hidden
   */
  #handleVisibilityChange = () => {
    try {
      if (document.hidden && this.#scrollLockedByMenu) {
        this.#enableBodyScroll();
      }
    } catch (error) {
      console.warn('HeaderMenu: Error in handleVisibilityChange', error);
    }
  };

  /**
   * Disable page scroll while the mega menu is open (desktop only).
   *
   * Locks with `overflow: hidden` on <html> (class jit-menu-scroll-lock, base.css). The page keeps its
   * scroll position, so nothing has to be restored on close. The old lock pinned <body> with
   * position: fixed + top: -scrollY and scrolled back on close; with html { scroll-behavior: smooth }
   * that "scroll back" was animated (the page visibly scrolled when the mouse left the menu), and
   * while pinned scrollY read 0, so the sticky header switched state as if at the top of the page.
   * html { scrollbar-gutter: stable } keeps the scrollbar's space, so nothing shifts sideways.
   */
  #disableBodyScroll = () => {
    if (this.#scrollLockedByMenu) return;
    const root = document.documentElement;
    // Another component (dialog / drawer) already locked scrolling: don't interfere.
    if (root.hasAttribute('scroll-lock') || window.getComputedStyle(document.body).position === 'fixed') return;
    root.classList.add('jit-menu-scroll-lock');
    this.#scrollLockedByMenu = true;
  };

  /**
   * Re-enable page scroll when the menu closes (only if this menu locked it).
   */
  #enableBodyScroll = () => {
    if (!this.#scrollLockedByMenu) return;
    document.documentElement.classList.remove('jit-menu-scroll-lock');
    this.#scrollLockedByMenu = false;
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // Disable body scroll when menu opens (desktop only) - already handled by mouseenter
    if (!this.#belowDesktopQuery?.matches) {
      this.#disableBodyScroll();
    }

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    
    // Enable body scroll when menu closes (after a small delay to ensure menu is fully closed)
    setTimeout(() => {
      try {
        if (this.ariaExpanded !== 'true' && this.#scrollLockedByMenu) {
          this.#enableBodyScroll();
        }
      } catch (error) {
        console.warn('HeaderMenu: Error enabling scroll in deactivate', error);
      }
    }, 100);
    
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily. Runs once, on the first pointerenter/focusin
   * of the menu (see connectedCallback), so the panel images are ready when it opens.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const descendant = element.querySelector('[ref="menuitem"]');
  if (descendant instanceof HTMLElement) return descendant;

  const selfOrAncestor = element.closest('[ref="menuitem"]');
  return selfOrAncestor instanceof HTMLElement ? selfOrAncestor : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
